import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, Save, ArrowLeft, Lightbulb, AlertCircle } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { usePostesIluminacao } from '../context/PostesIluminacaoProvider';
import { STATUS_POSTE, TIPOS_LAMPADA, POTENCIAS_LAMPADA, ALTURAS_POSTE } from '../utils/constants';
import { validarNumeroIdentificacao, validarCoordenadas } from '../utils/formatters';
import { createPosteIcon } from '../utils/mapHelpers';
import 'leaflet/dist/leaflet.css';

// Componente para selecionar localização no mapa
const LocationSelector = ({ position, setPosition }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
    }
  });

  return position ? (
    <Marker
      position={position}
      icon={createPosteIcon('Funcionando')}
    />
  ) : null;
};

export default function CadastroPoste() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdicao = !!id;

  const {
    localidades,
    criarPoste,
    atualizarPoste,
    buscarPostePorId
  } = usePostesIluminacao();

  // Localidades são carregadas automaticamente pelo contexto

  const [formData, setFormData] = useState({
    numero_identificacao: '',
    latitude: '',
    longitude: '',
    status: STATUS_POSTE.FUNCIONANDO,
    tipo_lampada: '',
    potencia: '',
    altura_poste: '',
    localidade_id: '',
    observacoes: '',
    data_instalacao: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPoste, setLoadingPoste] = useState(isEdicao);
  // Estados removidos - bairros são gerenciados centralmente

  // Estados para o mapa
  const [mapCenter, setMapCenter] = useState([-20.359292, -41.247282]); // Conceição do Castelo - ES
  const [zoom, setZoom] = useState(13);
  const [position, setPosition] = useState(null);

  // Carregar dados do poste para edição
  useEffect(() => {
    if (isEdicao) {
      carregarPoste();
    }
  }, [id, isEdicao]);

  // Sincronizar posição do mapa com os campos de coordenadas
  useEffect(() => {
    if (position) {
      setFormData(prev => ({
        ...prev,
        latitude: position[0].toString(),
        longitude: position[1].toString()
      }));
    }
  }, [position]);

  const carregarPoste = async () => {
    try {
      setLoadingPoste(true);
      const poste = await buscarPostePorId(id);
      setFormData({
        numero_identificacao: poste.numero_identificacao || '',
        latitude: poste.latitude?.toString() || '',
        longitude: poste.longitude?.toString() || '',
        status: poste.status || STATUS_POSTE.FUNCIONANDO,
        tipo_lampada: poste.tipo_lampada || '',
        potencia: poste.potencia || '',
        altura_poste: poste.altura_poste || '',
        localidade_id: poste.localidade_id?.toString() || '',
        observacoes: poste.observacoes || '',
        data_instalacao: poste.data_instalacao || ''
      });

      // Configurar posição no mapa
      if (poste.latitude && poste.longitude) {
        const pos = [parseFloat(poste.latitude), parseFloat(poste.longitude)];
        setPosition(pos);
        setMapCenter(pos);
        setZoom(15);
      }
    } catch (error) {
      console.error('Erro ao carregar poste:', error);
    } finally {
      setLoadingPoste(false);
    }
  };

  const handleInputChange = (campo, valor) => {
    setFormData(prev => ({
      ...prev,
      [campo]: valor
    }));

    // Atualizar posição do mapa quando coordenadas forem alteradas manualmente
    if (campo === 'latitude' || campo === 'longitude') {
      const lat = campo === 'latitude' ? parseFloat(valor) : parseFloat(formData.latitude);
      const lng = campo === 'longitude' ? parseFloat(valor) : parseFloat(formData.longitude);

      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        const newPos = [lat, lng];
        setPosition(newPos);
        setMapCenter(newPos);
      }
    }

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[campo]) {
      setErrors(prev => ({
        ...prev,
        [campo]: null
      }));
    }
  };

  const validarFormulario = () => {
    const novosErros = {};

    // Validar número de identificação
    const erroNumero = validarNumeroIdentificacao(formData.numero_identificacao);
    if (erroNumero) {
      novosErros.numero_identificacao = erroNumero;
    }

    // Validar coordenadas
    if (!position) {
      novosErros.coordenadas = 'Selecione a localização do poste no mapa';
    } else {
      const erroCoordenadas = validarCoordenadas(formData.latitude, formData.longitude);
      if (erroCoordenadas) {
        novosErros.coordenadas = erroCoordenadas;
      }
    }

    // Validar campos obrigatórios
    if (!formData.tipo_lampada) {
      novosErros.tipo_lampada = 'Tipo de lâmpada é obrigatório';
    }

    if (!formData.localidade_id) {
      novosErros.localidade_id = 'Bairro é obrigatório';
    }

    setErrors(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setLoading(true);
    try {
      const dadosPoste = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        localidade_id: parseInt(formData.localidade_id),
        data_instalacao: formData.data_instalacao || null
      };

      if (isEdicao) {
        await atualizarPoste(id, dadosPoste);
      } else {
        await criarPoste(dadosPoste);
      }

      navigate('/postes-iluminacao');
    } catch (error) {
      console.error('Erro ao salvar poste:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função removida - bairros são gerenciados centralmente



  if (loadingPoste) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando dados do poste...</p>
      </div>
    );
  }

  return (
    <div className="postes-iluminacao">
      <div className="cadastro-full-container">
        <div className="cadastro-header">
          <button onClick={() => navigate('/postes-iluminacao')} className="back-button">
            <ArrowLeft size={20} />
          </button>
          <div className="header-content">
            <h1>
              <Lightbulb size={24} color="#ff6f00" />
              {isEdicao ? 'Editar Poste' : 'Cadastrar Novo Poste'}
            </h1>
            <p>
              {isEdicao
                ? 'Atualize as informações do poste de iluminação'
                : 'Preencha os dados do novo poste de iluminação pública'
              }
            </p>
          </div>
        </div>

        <div className="cadastro-content-wrapper">

          <form onSubmit={handleSubmit} className="cadastro-form-optimized">
            {/* Layout Principal em Grid */}
            <div className="form-main-grid">
              {/* Coluna Esquerda - Formulário */}
              <div className="form-fields-column">

                {/* Seção 1: Identificação e Status */}
                <div className="form-section-compact">
                  <h3>Identificação</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="numero_identificacao">Número de Identificação *</label>
                      <input
                        type="text"
                        id="numero_identificacao"
                        value={formData.numero_identificacao}
                        onChange={(e) => handleInputChange('numero_identificacao', e.target.value)}
                        placeholder="Ex: 001, P001, etc."
                        className={errors.numero_identificacao ? 'error' : ''}
                      />
                      {errors.numero_identificacao && (
                        <span className="error-message">
                          <AlertCircle size={14} />
                          {errors.numero_identificacao}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="status">Status *</label>
                      <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                      >
                        {Object.values(STATUS_POSTE).map(status => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Seção 2: Bairro */}
                <div className="form-section-compact">
                  <h3>Bairro</h3>
                  <div className="form-group">
                    <label htmlFor="localidade_id">Bairro *</label>
                    <select
                      id="localidade_id"
                      value={formData.localidade_id}
                      onChange={(e) => handleInputChange('localidade_id', e.target.value)}
                      className={errors.localidade_id ? 'error' : ''}
                    >
                      <option value="">Selecione um bairro</option>
                      {localidades.map(localidade => (
                        <option key={localidade.id} value={localidade.id}>
                          {localidade.nome}
                        </option>
                      ))}
                    </select>
                    {errors.localidade_id && (
                      <span className="error-message">
                        <AlertCircle size={14} />
                        {errors.localidade_id}
                      </span>
                    )}
                  </div>
                </div>

                {/* Seção 3: Especificações Técnicas */}
                <div className="form-section-compact">
                  <h3>Especificações Técnicas</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="tipo_lampada">Tipo de Lâmpada *</label>
                      <select
                        id="tipo_lampada"
                        value={formData.tipo_lampada}
                        onChange={(e) => handleInputChange('tipo_lampada', e.target.value)}
                        className={errors.tipo_lampada ? 'error' : ''}
                      >
                        <option value="">Selecione o tipo</option>
                        {TIPOS_LAMPADA.map(tipo => (
                          <option key={tipo} value={tipo}>
                            {tipo}
                          </option>
                        ))}
                      </select>
                      {errors.tipo_lampada && (
                        <span className="error-message">
                          <AlertCircle size={14} />
                          {errors.tipo_lampada}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="potencia">Potência</label>
                      <select
                        id="potencia"
                        value={formData.potencia}
                        onChange={(e) => handleInputChange('potencia', e.target.value)}
                      >
                        <option value="">Selecione a potência</option>
                        {POTENCIAS_LAMPADA.map(potencia => (
                          <option key={potencia} value={potencia}>
                            {potencia}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="altura_poste">Altura do Poste</label>
                      <select
                        id="altura_poste"
                        value={formData.altura_poste}
                        onChange={(e) => handleInputChange('altura_poste', e.target.value)}
                      >
                        <option value="">Selecione a altura</option>
                        {ALTURAS_POSTE.map(altura => (
                          <option key={altura} value={altura}>
                            {altura}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="data_instalacao">Data de Instalação</label>
                      <input
                        type="date"
                        id="data_instalacao"
                        value={formData.data_instalacao}
                        onChange={(e) => handleInputChange('data_instalacao', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Seção 4: Coordenadas */}
                <div className="form-section-compact">
                  <h3>Coordenadas</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="latitude">Latitude *</label>
                      <input
                        type="number"
                        id="latitude"
                        step="any"
                        value={formData.latitude}
                        onChange={(e) => handleInputChange('latitude', e.target.value)}
                        placeholder="-20.123456"
                        className={errors.coordenadas ? 'error' : ''}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="longitude">Longitude *</label>
                      <input
                        type="number"
                        id="longitude"
                        step="any"
                        value={formData.longitude}
                        onChange={(e) => handleInputChange('longitude', e.target.value)}
                        placeholder="-40.123456"
                        className={errors.coordenadas ? 'error' : ''}
                      />
                    </div>
                  </div>

                  {position && (
                    <div className="coordinates-display">
                      <MapPin size={16} style={{ color: '#ff6f00' }} />
                      <strong>Coordenadas:</strong>&nbsp;{position[0].toFixed(6)}, {position[1].toFixed(6)}
                    </div>
                  )}
                </div>

                {/* Seção 5: Observações */}
                <div className="form-section-compact">
                  <h3>Observações</h3>
                  <div className="form-group">
                    <label htmlFor="observacoes">Observações Adicionais</label>
                    <textarea
                      id="observacoes"
                      value={formData.observacoes}
                      onChange={(e) => handleInputChange('observacoes', e.target.value)}
                      placeholder="Informações adicionais sobre o poste..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Coluna Direita - Mapa */}
              <div className="map-column">
                <div className="map-section">
                  <h4 className="map-title">
                    <MapPin size={18} />
                    Localização do Poste
                  </h4>

                  <p className="map-description">
                    Clique no mapa para selecionar a localização exata do poste de iluminação.
                  </p>

                  {errors.coordenadas && (
                    <div className="error-alert">
                      <AlertCircle size={14} />
                      {errors.coordenadas}
                    </div>
                  )}

                  <div className="map-container">
                    <MapContainer
                      center={mapCenter}
                      zoom={zoom}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <LocationSelector position={position} setPosition={setPosition} />
                    </MapContainer>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/postes-iluminacao')}
                className="button secondary"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="button primary"
              >
                <Save size={16} />
                {loading ? 'Salvando...' : (isEdicao ? 'Atualizar Poste' : 'Cadastrar Poste')}
              </button>
            </div>
          </form>

          {/* Modal removido - bairros são gerenciados centralmente */}
        </div>
      </div>
    </div>
  );
}
